/**
 * @typedef {number|{
 *  adult: number,
 *  child: number
 * }} ModelBookingPax
 *
 * @typedef {{
 *  discount: string,
 *  discount_type: 'percentage'|'amount'
 *  discounted_price: string
 * }} ModelDiscount
 *
 * @typedef {{
 *  price_type: 'by_age'|'by_trip'|'by_pax',
 *  currency: string,
 *  adult_rate: {
 *    [x: string]: string
 *  }
 *  child_rate: {
 *    [x: string]: string
 *  }
 * }} ModelPrice
 *
 * @typedef {{
 *  pax_max: number,
 *  [x: string]: any
 * }} ModelTripPax
 *
 * @typedef {{
 *  total: number,
 *  parking: number,
 *  price: number,
 *  pricePerPax?: number,
 *  priceByAge?: {
 *    adult: number,
 *    child: number
 *  },
 *  insurance?: void,
 *  insurancePerPax?: void,
 *  taxAmount?: void,
 *  discount?: number,
 *  discountType?: 'percentage' | 'amount',
 *  deduction?: number,
 *  promos?: Array<{
 *    promoValid: boolean,
 *    discountAmount: number,
 *    promoCode: string,
 *    message: string,
 *    isFree: boolean,
 *    desc: string
 *  }>
 * }} ModelBookingPrice
 */

import moment from 'moment';

/**
 * Truncates string that is longer than the specified length and appends '...' to the end of the string.
 * @param {string} str String to truncate.
 * @param {number} maxLen Maximum resulting string length, not including the final three dots.
 * @returns {string} Truncated string with ellipsis if the string matches the condition.
 */
export function ellipsis(str, maxLen) {
  return str.length <= maxLen ? str : str.slice(0, maxLen).concat('...');
}

/**
 * Extract the first paragraph of a HTML content.
 * If the extracted paragraph is longer than the specified length (defaults at 200), the first sentence is returned.
 * @param {string} html HTML string of content.
 * @param {number?} maxLen Maximum length of the chunk. Defaults to 200.
 * @returns {string} Extracted content chunk wrapped in `<p>`.
 */
export function extractFirstContentChunk(html, maxLen = 200) {
  if (html.indexOf('</p>') === -1 && html.indexOf('.') === -1) {
    return html;
  }

  const firstParagraph = html.split('</p>')[0];
  maxLen += 3; // The opening <p> tag has lenght of 3

  if (firstParagraph.length <= maxLen) {
    return firstParagraph.concat('</p>');
  }

  const firstSentence = firstParagraph.split('.')[0];
  return firstSentence.concat('.</p>');
}

/**
 * Integer parser that works with thousand separators and compatible with different locales.
 * @param {string} value Number string.
 * @param {string} locale Locale to parse.
 * @returns {number}
 */
export function int(value, locale = navigator.language) {
  const normalized = normaliseNumber(value, locale);
  return parseInt(normalized);
}

/**
 * Float parser that works with thousand separators and compatible with different locales.
 * @param {string} value Number string.
 * @param {string} locale Locale to parse.
 * @returns {number}
 */
export function float(value, locale = navigator.language) {
  const normalized = normaliseNumber(value, locale);
  return parseFloat(normalized);
}

/**
 * Normalise number format across locale.
 * @param {string} value Number string.
 * @param {string} locale Locale to parse.
 * @returns {string} Normalised number string.
 */
export function normaliseNumber(value, locale) {
  try {
    if (typeof value !== 'string') {
      value = value.toString();
    }
    const example = Intl.NumberFormat(locale).format('1.1');
    const cleanPattern = new RegExp(`[^-+0-9${example.charAt(1)}]`, 'g');
    const cleaned = value.replace(cleanPattern, '');
    return cleaned.replace(example.charAt(1), '.');
  } catch (_) {
    return value;
  }
}

/**
 * Appends a 'mini' class span to vendor name.
 * Expects a vendor name to be like '<name> (<ID>)'.
 * @param {string} vendor Original vendor name.
 * @returns {string} Transformed vendor name.
 */
export function transformVendorName(vendor) {
  if (vendor.indexOf('(') === -1) {
    return vendor;
  }
  const [name, id] = vendor.split('(');
  return name.concat(`<span class="fe-mini">(${id}</span>`);
}

/**
 * Transform vendor name into the format of '<name> (<id>)'.
 * @param {string} vendor
 * @param {string|number|void} vendorNo
 * @returns {string} Formatted vendor name.
 */
export function formatVendorName(vendor, vendorNo) {
  return vendorNo
    ? `${vendor} <span class="fe-mini">(${vendorNo})</span>`
    : vendor;
}

/**
 * Transform vendor name into the format of '<name> (<id>) in bold'.
 * @param {string} vendor
 * @param {string|number|void} vendorNo
 * @returns {string} Formatted vendor name.
 */
export function formatVendorNameBold(vendor, vendorNo) {
  return vendorNo
    ? `<strong>${vendor}</strong> <span class="fe-mini"><b>(${vendorNo})</b></span>`
    : vendor;
}

/**
 * Parses date strings like 2019-05-13 into a Date object.
 * @param {string=} date Raw date string.
 * @returns {null|Date} Date object or null if `date` is not a string.
 */
export function parseStringToDate(str) {
  if (typeof str !== 'string') {
    return null;
  }
  const [year, month, date] = str.split('-').map(n => int(n));
  return new Date(
    year,
    month - 1, // -1 because month index starts at 0.
    date
  );
}

/**
 * Formats 'YYYY-MM-DD' into 'dD mM YYYY' (default) or as specified.
 * @param {string} str Raw date string.
 * @param {string=} format Target date format. `format` should be a format recognised by momentjs. Defaults to 'D MMMM YYYY'.
 * @returns {string} Formatted date string.
 */
export function formatDate(str, format = 'D MMMM YYYY') {
  return moment(str).format(format);
}

/**
 * Parse date object into YYYY-MM-DD string.
 * @param {Date} date Date object to parse.
 */
export function parseDateToString(dateObj) {
  const year = dateObj.getFullYear();
  const month = dateObj.getMonth() + 1;
  const date = dateObj.getDate();
  return [year, month, date].map(n => (n < 10 ? '0' + n : n)).join('-');
}

/**
 * Creates an array of number from the given minimum and maximum, both inclusive.
 * @param {number} min Minimum number (inclusive).
 * @param {number} max Maximum number (inclusive).
 */
export function range(min, max) {
  const ret = [];
  for (let i = min; i <= max; i++) {
    ret.push(i);
  }
  return ret;
}

/**
 * Get cookie by name.
 * @param {string} cname Target cookie name.
 * @returns {string}
 */
export function getCookie(cname) {
  var name = cname + '=';
  var decodedCookie = decodeURIComponent(document.cookie);
  var ca = decodedCookie.split(';');
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
}

/**
 * Check if an object is empty.
 * @param {*} obj Object to check.
 */
export function isEmptyObject(obj) {
  try {
    return JSON.stringify(obj) === '{}';
  } catch (e) {
    return false;
  }
}

/**
 * Convert first letter of each word to uppercase.
 * @param {string} str Sentence to capitalise.
 * @returns {string} Converted string.
 */
export function capitalise(str) {
  return str
    .split(' ')
    .map(
      w =>
        w &&
        w
          .charAt(0)
          .toUpperCase()
          .concat(w.slice(1))
    )
    .join(' ');
}

/**
 * Extract specified properties from a target object as a shallow new object.
 * If any of the specified properties are not found in the target object as its own properties, the property will not be included in the plucked object.
 * @param {Object.<string, *>} obj Target object.
 * @param  {...string} props Properties to pluck from object.
 * @returns {Object.<string, *>} A shallow new object with the specified properties.
 */
export function pluck(obj, ...props) {
  const hasOwnProp = Object.prototype.hasOwnProperty.bind(obj);
  return props.reduce((ret, prop) => {
    if (hasOwnProp(prop)) {
      ret[prop] = obj[prop];
    }
    return ret;
  }, {});
}

/**
 * Shuffle the order of items in an array.
 * This modifies the original array.
 * @param {*[]} list
 * @returns {*[]} The shuffled original array.
 */
export function shuffle(list) {
  const { length } = list;
  const rand = length => ~~(Math.random() * length);
  let i = length;

  while (i--) {
    const a = rand(length);
    const b = rand(length);
    const t = list[a];
    list[a] = list[b];
    list[b] = t;
  }

  return list;
}

/* PRICE CALCULATION **/

/**
 * Simple price calculation without SST
 * @param {{
 *  trip_price: ModelPrice,
 *  trip_discount: ModelDiscount,
 *  pax_max: number
 * }} bookingInfo
 * @param {ModelBookingPax} bookingPax
 * @returns {ModelBookingPrice}
 */
export function calcSimplePrice(bookingInfo, bookingPax) {
  const { trip_price, trip_discount, pax_max } = bookingInfo;
  const { price_type, parking } = trip_price;
  const isByAge = price_type === 'by_age';
  const isByPax = price_type === 'by_pax';
  const price = calcTripPrice(trip_price, bookingPax);

  /**
   * @type {ModelBookingPrice}
   */
  const ret = {};

  ret.parking = float(parking);
  ret.price = price;
  ret[isByAge ? 'priceByAge' : 'pricePerPax'] = getPricePerPax(trip_price, {
    pax_max
  });

  if (isByPax) {
    ret.adult_rate = bookingInfo.trip_price.adult_rate;
    ret.child_rate = bookingInfo.trip_price.child_rate
      ? bookingInfo.trip_price.child_rate
      : bookingInfo.trip_price.adult_rate;
  }

  ret.total = ret.price + ret.parking;
  ret.taxAmount = null;

  if (trip_discount) {
    ret.discount = float(trip_discount.discount);
    ret.discountType = trip_discount.discount_type;
    ret.deduction =
      ret.discountType === 'percentage'
        ? ret.total * ret.discount
        : ret.discountType === 'amount'
        ? ret.discount
        : 0;
    ret.total -= ret.deduction;
  }

  return ret;
}

/**
 * Converts API response into correct pricing object format
 * @param {{
 *  currency: string,
 *  discount: Array<{
 *    amount: string,
 *    description: string | null,
 *    discount_type: 'percentage' | 'amount',
 *    is_free: boolean,
 *    is_valid: boolean,
 *    message: string,
 *    percentage: string,
 *    promo_code: string
 *  }>
 *  insurance_price: string,
 *  total_insurance_price: string,
 *  is_per_trip: boolean,
 *  parking_price: string,
 *  price_per_pax: string | {
 *    adult: string,
 *    child: string
 *  },
 *  price_tax: string,
 *  price_type: 'by_age' | 'by_trip',
 *  total_price: string,
 *  trip_discount: {
 *    deducted_amount: string,
 *    discount: string,
 *    discount_type: 'percentage' | 'amount',
 *    discounted_amount: string
 *  },
 *  trip_price: string
 * }} priceObj
 * @returns {ModelBookingPrice}
 */
export function updateBookingPriceFromResponse(priceObj) {
  const {
    discount,
    insurance_price,
    total_insurance_price,
    parking_price,
    price_per_pax,
    price_tax,
    price_type,
    total_price,
    trip_discount,
    trip_price
  } = priceObj;

  /**
   * @type {ModelBookingPrice}
   */
  const ret = {};

  if (price_type !== 'by_age') {
    if (price_per_pax.adult){
      ret.pricePerPax = Object.entries(price_per_pax).reduce((ret, [key, val]) => {
        ret[key] = int(val);
        return ret;
      }, {});
    }else{
      ret.pricePerPax = int(price_per_pax);
    }
  } else {
    ret.priceByAge = Object.entries(price_per_pax).reduce((ret, [key, val]) => {
      ret[key] = int(val);
      return ret;
    }, {});
  }

  if (trip_discount) {
    ret.discount = float(trip_discount.discount);
    ret.discountType = trip_discount.discount_type;
    ret.deduction = float(trip_discount.deducted_amount);
  }

  if (discount) {
    ret.promos = (Array.isArray(discount) ? discount : [discount]).map(obj => ({
      promoValid: obj.is_valid,
      discountAmount: float(obj.amount),
      promoCode: obj.promo_code,
      message: obj.message,
      isFree: obj.is_free,
      desc: obj.description
    }));
  } else {
    ret.promos = [];
  }
  ret.insurancePerPax = float(insurance_price);
  ret.insurance = float(total_insurance_price);
  ret.taxAmount = float(price_tax);
  ret.price = float(trip_price);
  ret.parking = float(parking_price);
  ret.total = float(total_price);
  ret.discount = priceObj.byor_discount
    ? float(priceObj.byor_discount.amount)
    : null;

  return ret;
}

export function getRandomInt(max) {
  let randInt = Math.floor(Math.random() * Math.floor(max));
  return randInt;
}

export function getAuthErr(exception) {
  switch (exception) {
    case 'UserNotConfirmedException':
      return 'userNotConfirmed';
    case 'InvalidPasswordException':
      return 'invalidPassword';
    case 'UsernameExistsException':
      return 'usernameExists';
    case 'UserNotFoundException':
      return 'userNotFound';
    case 'TooManyRequestsException':
      return 'tooManyRequests';
    case 'TooManyFailedAttemptsException':
      return 'tooManyFails';
    case 'NotAuthorizedException':
      return 'notAuthorized';
    case 'CodeMismatchException':
      return 'codeMismatch';
    case 'TooManySignInAttemptsException': //custom handler, need to check NotAuthorizedException message
      return 'tooManySignIns';
    case 'UserDisabledException': //custom handler
      return 'userDisabled';
    default:
      return 'genericAuthError';
  }
}

export function getDiscDeduction(amount, discountType, discount) {
  let tripAmount = float(amount);
  let discountAmount = float(discount);
  if (discountType === 'percentage') {
    return tripAmount * discountAmount;
  } else {
    // fixed amount discount e.g. RM 50 off
    return discountAmount;
  }
}

export function findMinMaxPaxRate(pax_rate, type){
  const pax_rate_keys = Object.keys(pax_rate);
  const optionsAdult = [];
  pax_rate_keys.forEach(key => {
    switch (key){
      case 'one_pax':
        optionsAdult.push(1);
        break;
      case 'two_pax':
        optionsAdult.push(2);
        break;
      case 'three_pax':
        optionsAdult.push(3);
        break;
      case 'four_pax':
        optionsAdult.push(4);
        break;
    }
  });
  if(type === 'max'){
    return Math.max(...optionsAdult);
  }
  else{
    return Math.min(...optionsAdult);
  }
}

/**
 * @param {ModelPrice} priceModel
 * @param {ModelBookingPax} bookingPax
 */
export function calcTripPrice(priceModel, bookingPax) {
  const {
    amount,
    child,
    adult,
    price_type,
    is_per_trip,
    adult_rate,
    child_rate
  } = priceModel;

  const highest_adult_rate = findMinMaxPaxRate(adult_rate, 'max');
  if (bookingPax.adult){
    if (bookingPax.adult > highest_adult_rate){
      bookingPax.adult = highest_adult_rate;
    }
  }
  else{
    if (bookingPax > highest_adult_rate){
      bookingPax = highest_adult_rate;
    }
  }

  let ret = 0;

  if (price_type === 'by_age') {
    const amountAdult = float(adult);
    const amountChild = float(child);
    const paxAdult = int(bookingPax.adult);
    const paxChild = int(bookingPax.child);
    ret += paxAdult * amountAdult;
    ret += paxChild * amountChild;
  } else if (price_type === 'by_pax') {
    const adultPax = int(bookingPax.adult) || bookingPax;
    const childPax = int(bookingPax.child) || 0;
    const amountAdult = float(adult_rate[paxParser(int(adultPax))]);
    ret += amountAdult;
    if (child_rate) {
      const amountChild = float(child_rate[paxParser(int(childPax))]) || 0;
      ret += amountChild;
    }
  } else {
    ret += float(amount);
    if (!is_per_trip) {
      ret *= int(bookingPax);
    }
  }

  return ret;
}

/**
 * @param {ModelPrice} priceModel
 * @param {ModelBookingPax} bookingPax
 * @param {ModelDiscount} discountModel
 */
export function calcTripDiscountedPrice(priceModel, bookingPax, discountModel) {
  const { discount, discount_type } = discountModel || {};
  const price = calcTripPrice(priceModel, bookingPax);
  return typeof discount_type === 'string'
    ? discount_type === 'percentage'
      ? price - price * float(discount)
      : price - float(discount)
    : price;
}

/**
 * @param {ModelPrice} priceModel
 * @param {ModelTripPax} _
 * @returns {ModelBookingPax}
 */
export function getPricePerPax(priceModel) {
  if (
    priceModel.price_type === 'by_trip' ||
    priceModel.price_type === 'by_age'
  ) {
    const { amount, child, adult, price_type, is_per_trip } = priceModel || {};

    if (price_type !== 'by_age') {
      return !is_per_trip ? float(amount) : float(amount);
    }

    return {
      adult: float(adult),
      child: float(child)
    };
  } else if (priceModel.price_type === 'by_pax') {
    const { adult_rate, child_rate } = priceModel || {};

    if (child_rate === undefined) {
      return {
        adult: float(adult_rate[Object.keys(adult_rate)[0]])
      };
    } else {
      return {
        adult: float(adult_rate[Object.keys(adult_rate)[0]]),
        child: float(child_rate[Object.keys(child_rate)[0]])
      };
    }
  }
}

export function paxParser(index) {
  switch (index) {
    case 1:
      return 'one_pax';
    case 2:
      return 'two_pax';
    case 3:
      return 'three_pax';
    case 4:
      return 'four_pax';

    default:
      break;
  }
}

/**
 * @param {ModelPrice} priceModel
 * @param {ModelBookingPax} bookingPax
 * @returns {ModelBookingPax}
 */
export function getCurrentPaxPrice(priceModel, bookingPax) {
  const { adult_rate, child_rate } = priceModel || {};
  let paxKey;

  switch (bookingPax) {
    case 1:
      paxKey = 'one_pax';
      break;
    case 2:
      paxKey = 'two_pax';
      break;
    case 3:
      paxKey = 'three_pax';
      break;
    case 4:
      paxKey = 'four_pax';
      break;

    default:
      break;
  }

  return {
    adult: float(adult_rate[paxKey]),
    child: float(child_rate[paxKey])
  };
}

/**
 * @param {ModelPrice} priceModel
 * @param {ModelTripPax} _
 * @returns {ModelBookingPax}
 */
export function getPriceByPax(priceModel) {
  const { child_rate, adult_rate } = priceModel || {};

  return {
    adult: float(adult_rate[Object.keys(adult_rate)[0]]),
    child: float(child_rate[Object.keys(child_rate)[0]])
  };
}

/**
 * @param {ModelPrice} priceModel
 * @param {ModelTripPax} tripPax
 * @param {ModelDiscount} discountModel
 * @returns {ModelBookingPax|null}
 */
export function getDiscPricePerPax(priceModel, tripPax, discountModel) {
  if (!discountModel) {
    return null;
  }

  const { discount, discount_type } = discountModel;
  const price = getPricePerPax(priceModel, tripPax);
  const calc = price => {
    switch (discount_type) {
      case 'percentage':
        return price - price * discount;
      case 'amount':
        return price - discount;
      default:
        return price;
    }
  };

  return typeof price === 'number'
    ? calc(price)
    : {
        adult: calc(price.adult),
        child: calc(price.child)
      };
}

/**
 * @param {ModelBookingPax} price
 * @param {ModelBookingPax} discount
 * @returns {string|{
 *  adult: string,
 *  child: string
 * }}
 */
export function createPriceLabel(price, discount) {
  const create = (price, discount) =>
    discount
      ? `RM <span class="fe-old-price">${parseMoney(
          price
        )}</span><span class="fe-new-price">${parseMoney(discount)}</span>`
      : `RM <span>${parseMoney(price)}</span>`;

  return typeof price === 'number'
    ? create(price, discount)
    : price.child != null
    ? {
        adult: create(price.adult, discount && discount.adult),
        child: create(price.child, discount && discount.child)
      }
    : { adult: create(price.adult, discount && discount.adult) };
}

/**
 * @param {ModelDiscount} tripDiscount
 * @returns {string}
 */
export function createDiscountLabel(tripDiscount) {
  return tripDiscount
    ? tripDiscount.discount_type === 'percentage'
      ? `${float(tripDiscount.discount) * 100}%`
      : `RM ${parseMoney(float(tripDiscount.discount), 0)}`
    : '';
}

/**
 * Parse number into money-formatted string.
 * @param {number} amount Price.
 * @param {number?} decimals Resulting decimal places. Defaults to 2.
 */
export function parseMoney(amount, decimals = 2) {
  return float(amount)
    .toFixed(decimals)
    .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Lotame pixel thing provided by client. Script is manually loaded in views/frontend/app.blade.php
 */
export function lotamePixel() {
  if (window._cc10587 && process.env.APP_ENV === 'production') {
    window._cc10587.bcp();
  }
}

export function parseQueryString() {
  const qs = window.location.search;

  if (qs[0] !== '?') {
    return {};
  }

  return qs
    .slice(1)
    .split(/&/g)
    .map(pair => pair.split('=').map(window.decodeURIComponent))
    .reduce((ret, [key, val]) => {
      ret[key] = val;
      return ret;
    }, {});
}

/**
 * Search a string for a specified email and replace it with a mailto anchor tag if found.
 * If the second argument `str` is not supplied or is not a string, a curried version of the function will be returned.
 * The curried version can be used as a measure to minimise memory footprint (i.e., when used inside an iteration).
 * @param {string} email Email to parse.
 * @param {string=} str String to search.
 * @returns {((str: string) => string) | string} Returns a curried function that accepts a single `str` argument as the string to search if `str` is not a string. Otherwise, returns the searched/replaced string.
 */
export function mailto(email, str) {
  const regex = new RegExp(`(^|\\b)${email}(\\b|$)`, 'ig');
  const replacer = (regex, str) =>
    typeof str === 'string'
      ? str.replace(regex, '<a href="mailto:$&">$&</a>')
      : str;

  if (arguments.length > 1) {
    return replacer(regex, str);
  }

  // Binding `regex` and `replacer` to the curried function to prevent memory leak
  return function(regex, replacer, str) {
    return replacer(regex, str);
  }.bind(null, regex, replacer);
}

export function lastOf(list) {
  return Array.isArray(list) ? list[list.length - 1] : null;
}

export function getLaunchVoteItem(id, list) {
  var result = list.filter(obj => {
    return obj.id === id;
  });
  return result[0];
}

/**
 * Get the remaining strings after first coma found.
 * @param {string} str
 * @returns {string} str
 */
export function getFirstCharAfterComa(str) {
  return str.substr(str.indexOf(',') + 1);
}

export function sortState(a, b) {
  return getFirstCharAfterComa(a).localeCompare(getFirstCharAfterComa(b));
}

export function combineStr(trip) {
  let locationArr = trip.location_text.split(', ');
  if (locationArr.length > 1) {
    return locationArr[1].concat(locationArr[0]).concat(trip.slug);
  } else {
    return locationArr[0].concat(trip.slug);
  }
}
